import { debounce } from 'lodash'
import { differenceInDays } from 'date-fns'
import { X, ChevronDown } from 'react-feather'
import { Sparkle, Funnel, Tag, Clock } from '@phosphor-icons/react'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from '@shopflo/ui'
import { initialOverviewFilterData, overviewFilterConfig } from 'src/assets/AnalyticsConfig'
import FloFilters from 'src/components/common/filter-component/FloFilters'
import NewDatePicker from 'src/components/common/search-and-filters/subcomponents/NewDatePicker'
import { useAnalytics } from 'src/lib/contexts/AnalyticsContext'
import { useFilter } from 'src/lib/contexts/FilterContext'
import { useLocale } from 'src/lib/hooks/useLocal'
import { FilterConfigType, FilterDataType, TabType } from 'src/lib/types/filters'
import { extractDateFromFilters } from 'src/lib/utils/analyticsHelpers'
import { dashboardTabs, permissionType } from 'src/lib/utils/constants'
import { appliedDateFilter, classNames, getDateTime } from 'src/lib/utils/helpers'
import { useOrganisationContext } from 'src/lib/contexts/OrganisationContext'
import { useUser } from 'src/lib/contexts/UserContext'
import { triggerFunnelAnalysis, setMerchantAttributes } from 'src/lib/core/api-methods'
import { useEffect, useMemo, useState } from 'react'
import { successToast } from 'src/lib/utils/toasters'
import SyncStatus from 'src/components/sso-analytics/common/SyncStatus'
import { useMerchant } from 'src/lib/contexts/MerchantContext'

const COOLDOWN_DAYS = 7

export const Filters = () => {
  const { t } = useLocale()
  const {
    getters: { dateFilter, refreshKey, channelData: filterData },
    actions: { setDateFilter, setChannelData: setFilterData, handleDeleteColumnValue, setRefreshKey }
  } = useAnalytics()
  const { state: organisationData } = useOrganisationContext()
  const { user, canAccess } = useUser()
  const {
    getters: { merchant },
    actions: { mutateMerchantAttributes }
  } = useMerchant()
  const [isFunnelLoading, setIsFunnelLoading] = useState(false)
  const [isSyncing, setIsSyncing] = useState(false)
  const [lastSyncedAt, setLastSyncedAt] = useState<Date | null>(null)

  useEffect(() => setLastSyncedAt(new Date()), [])
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)

  const { setAnalyticsV2FiltersCtx } = useFilter()

  const hasAiAuditAccess = canAccess(permissionType.TAB_AI_AUDIT, 0)
  const hasUiAuditAccess = canAccess(permissionType.UI_AI_AUDIT, 0)
  const hasDiscountAuditAccess = canAccess(permissionType.DISCOUNT_AI_AUDIT, 0)

  const cooldownInfo = useMemo(() => {
    if (!user?.email || !merchant) return { isDisabled: false, remainingDays: 0 }

    const funnelAnalysisData = merchant?.attributes?.funnelAnalysisRequests?.[user.email]
    if (!funnelAnalysisData?.requested_at) return { isDisabled: false, remainingDays: 0 }

    const daysPassed = differenceInDays(new Date(), new Date(funnelAnalysisData.requested_at))
    const remainingDays = COOLDOWN_DAYS - daysPassed

    return {
      isDisabled: remainingDays > 0,
      remainingDays: Math.max(0, remainingDays)
    }
  }, [user?.email, merchant])

  const handleRefresh = debounce(() => {
    setIsSyncing(true)
    setLastSyncedAt(new Date())
    setRefreshKey(refreshKey + 1)
    setDateFilter({
      ...dateFilter,
      lastUpdated: `${getDateTime(new Date()).date} at ${getDateTime(new Date()).time}`
    })
    window.setTimeout(() => setIsSyncing(false), 1500)
  }, 1000)

  const handleDateFilterChange = (dateFilter: FilterConfigType) => {
    setDateFilter(dateFilter)
    setAnalyticsV2FiltersCtx(dateFilter)
  }

  const handleFunnelAnalysis = async () => {
    if (!organisationData?.id || !user?.email || cooldownInfo.isDisabled) return
    setIsFunnelLoading(true)
    setIsDropdownOpen(false)
    try {
      await triggerFunnelAnalysis({
        shop_name: organisationData.name,
        merchant_id: organisationData.id,
        email: user.email,
        merchant_category: organisationData.taxDetails?.category || 'Other'
      })

      const existingRequests = merchant?.attributes?.funnelAnalysisRequests || {}
      await setMerchantAttributes(
        {
          attributes: {
            funnel_analysis_requests: {
              ...existingRequests,
              [user.email]: {
                email: user.email,
                merchant_category: organisationData.taxDetails?.category || 'Other',
                merchant_id: organisationData.id,
                shop_name: organisationData.name,
                requested_at: new Date().toISOString()
              }
            }
          }
        },
        merchant?.accountId ?? ''
      )
      mutateMerchantAttributes()

      successToast(t('funnel_analysis_sent_to', { email: user.email }))
    } catch (error) {
      console.error('Funnel analysis error:', error)
    } finally {
      setIsFunnelLoading(false)
    }
  }

  const isButtonDisabled = isFunnelLoading || cooldownInfo.isDisabled

  return (
    <div className='flex flex-col gap-2'>
      <div className='flex items-center justify-between gap-2'>
        <div className='flex items-center gap-2'>
          <h1 className='text-text-lg-semibold text-zinc-800'>Analytics</h1>
          <SyncStatus lastSyncedAt={lastSyncedAt} isSyncing={isSyncing} onRefresh={handleRefresh} />
        </div>

        <div className='flex items-center gap-3'>
          <FloFilters
            startDate={extractDateFromFilters(dateFilter, 'startDate')}
            endDate={extractDateFromFilters(dateFilter, 'endDate')}
            filterConfig={overviewFilterConfig}
            initialFilterData={initialOverviewFilterData}
            filterArrayData={filterData}
            setFilterArrayData={setFilterData}
            isQueryBasedFilter={false}
            buttonClassName='flex items-center gap-1 px-3 py-2 text-sm font-semibold transition duration-200 bg-transparent border rounded whitespace-nowrap h-[30px] border-zinc-200 hover:bg-zinc-50'
          />

          <NewDatePicker
            dateFilter={dateFilter}
            setDateFilterCtx={handleDateFilterChange}
            appliedFilter={appliedDateFilter(dateFilter)}
            minDateValue={new Date(2022, 8, 20)}
            maxDateValue={new Date()}
            parentComponent={dashboardTabs.FLO_ANALYTICS_TAB_V2 as TabType}
            comparison
            hasCustomComparisons
          />

          {hasAiAuditAccess && (
            <DropdownMenu open={isDropdownOpen} onOpenChange={setIsDropdownOpen}>
              <DropdownMenuTrigger asChild>
                <button
                  id='ai-audit-button'
                  className='relative flex items-center gap-2 px-4 h-9 rounded-lg font-semibold text-sm overflow-hidden
             bg-ai-audit-btn shadow-ai-audit text-[rgba(9,9,11,0.7)] tracking-[-0.01em]'
                >
                  <span className='absolute inset-0 bg-ai-audit-shimmer bg-[length:250%_250%] blur-md animate-shimmer' />

                  <Sparkle size={18} weight='fill' className='text-[#00D492] relative z-10' />
                  <span className='relative z-10'>{isFunnelLoading ? 'Loading...' : 'AI Audit'}</span>
                  <ChevronDown
                    size={18}
                    className={classNames(
                      'text-[#71717B] transition-transform relative z-10',
                      isDropdownOpen ? 'rotate-180' : ''
                    )}
                  />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                className='w-72 p-2 bg-white rounded-xl shadow-lg border border-gray-light'
                align='end'
                sideOffset={8}
              >
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      onClick={handleFunnelAnalysis}
                      disabled={isButtonDisabled}
                      className={classNames(
                        'w-full flex items-start gap-3 p-3 rounded-lg text-left transition-colors',
                        isButtonDisabled ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-lightest cursor-pointer'
                      )}
                    >
                      <Funnel size={20} weight='duotone' className='text-primary-dark mt-0.5 shrink-0' />
                      <div className='flex flex-col'>
                        <span className='text-sm font-medium text-coal-dark'>Audit your conversion funnel</span>
                        <span className='text-xs text-coal-light'>Analyse drop offs with Shopflo AI</span>
                      </div>
                    </button>
                  </TooltipTrigger>
                  {cooldownInfo.isDisabled && (
                    <TooltipContent side='left' className='bg-carbon text-white px-3 py-2 rounded shadow text-xs'>
                      {t(
                        cooldownInfo.remainingDays > 1
                          ? 'funnel_analysis_available_in_days_plural'
                          : 'funnel_analysis_available_in_days',
                        { days: cooldownInfo.remainingDays }
                      )}
                    </TooltipContent>
                  )}
                </Tooltip>

                {(hasUiAuditAccess || hasDiscountAuditAccess) && (
                  <div className='my-2 border-t border-dashed border-gray-light' />
                )}

                {hasUiAuditAccess && (
                  <div className='flex items-start gap-3 p-3 rounded-lg opacity-60'>
                    <Sparkle size={20} weight='duotone' className='text-primary-dark mt-0.5 shrink-0' />
                    <div className='flex flex-col flex-1'>
                      <span className='text-sm font-medium text-coal-dark'>{t('audit_your_checkout_ui')}</span>
                    </div>
                    <span className='flex items-center gap-1 text-xs text-coal-light bg-gray-lightest px-2 py-0.5 rounded-full'>
                      <Clock size={12} weight='bold' /> {t('coming_soon')}
                    </span>
                  </div>
                )}

                {hasDiscountAuditAccess && (
                  <div className='flex items-start gap-3 p-3 rounded-lg opacity-60'>
                    <Tag size={20} weight='duotone' className='text-primary-dark mt-0.5 shrink-0' />
                    <div className='flex flex-col flex-1'>
                      <span className='text-sm font-medium text-coal-dark'>{t('audit_your_discounts')}</span>
                    </div>
                    <span className='flex items-center gap-1 text-xs text-coal-light bg-gray-lightest px-2 py-0.5 rounded-full'>
                      <Clock size={12} weight='bold' /> {t('coming_soon')}
                    </span>
                  </div>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </div>
      <div className='flex flex-wrap w-full h-full gap-2'>
        {filterData?.map((data: FilterDataType, index: number) => {
          return data?.values?.map((value: any, valueIndex: number) => (
            <div
              className='rounded-lg text-sm bg-gray-lightest text-coal-light border border-gray-light flex px-2 py-1 items-center gap-2'
              key={`${valueIndex}_${value?.id}`}
            >
              <span className='text-sm text-coal-dark'>
                Channel is <span className='text-sm font-medium text-primary-dark'>{value?.name}</span>
              </span>
              <X
                className='stroke-[2.5px] text-[#6e6e6e] w-4 h-4 mt-[2px] cursor-pointer'
                onClick={() => handleDeleteColumnValue(value?.id, index)}
              />
            </div>
          ))
        })}
      </div>
    </div>
  )
}
